/**
 * saveGuards.js
 * PIPE-SAVE-1 hotfix guard (2026-07-09 incident). `saveStInterviewStory` takes an optional
 * `processedOverride` — the freshly generated story object passed by the voice auto-save path, so
 * the save doesn't read a not-yet-flushed `stProcessed`. The manual Save button was wired
 * `onClick={saveStInterviewStory}`, so React passed the click Event as that argument; the Event was
 * truthy and got used as the story, making title/content undefined and breaking EVERY manual save
 * in production.
 *
 * `isProcessedStoryOverride` is the single predicate deciding whether an argument is a REAL processed
 * story to save (an object with string `content`) versus anything else — a DOM/React event, null,
 * `{}` — which must be ignored so the caller falls back to current state. Extracted here (not inline
 * in App.js) so it has a real unit test; App.js can't be loaded in Jest (react-pdf ESM).
 *
 * `checkSaveArtifact` (F-268b) is the second guard in this module: PIPE-OUT-1's substance floor asked
 * about the FINAL artifact, at the save choke point, instead of only about the raw model text.
 */
import { isUsableGeneration } from './interviewEngine';

export function isProcessedStoryOverride(arg) {
  return !!arg && typeof arg === 'object' && typeof arg.content === 'string';
}

/**
 * F-268b — THE SUBSTANCE FLOOR, ASKED AT THE ARTIFACT (F-153 class, 3rd live instance).
 *
 * This is NOT a new floor. It is `isUsableGeneration` — PIPE-OUT-1's own predicate — asked about the
 * text that is actually about to become a book page. The floor was never the missing piece: it
 * already refuses the shapes that reached production. What was missing was anyone asking it here.
 *
 * WHY A SECOND CALL SITE IS NECESSARY, not belt-and-braces. `chooseGeneration` (`App.js:5299`) runs
 * the floor on the RAW model text. Three things then rewrite that text before it is persisted —
 * `extractTitleContent`, the deterministic duplicate-title strip, and the F-116 output gate's
 * corrective regen / fail-closed fallback — and `saveStInterviewStory` writes `p.content` with no
 * floor between. Row `1b91ab99` (2026-07-30) went through that gap: PIPE-OUT-1 recomputed on its
 * SAVED content reads `usable=NO reason=truncated`. Checking the input to a transformation is not
 * checking its output.
 *
 * FAIL CLOSED, and deliberately so: anything that is not a string is refused rather than coerced, so
 * a shape this function does not understand can never become a keepsake. Reason strings are passed
 * through unchanged from PIPE-OUT-1 so the two can never drift into separate vocabularies.
 *
 * Extracted here for the same reason as `isProcessedStoryOverride` above: App.js cannot be loaded in
 * Jest (react-pdf ESM), so a decision that lives inline in App.js cannot have a real unit test.
 *
 * @param {string} content — the FINAL artifact, exactly as it will be persisted.
 * @returns {{ savable: boolean, reason: 'ok'|'empty'|'too-short'|'truncated'|'not-text' }}
 */
export function checkSaveArtifact(content) {
  if (typeof content !== 'string') return { savable: false, reason: 'not-text' };
  const r = isUsableGeneration(content);
  return { savable: r.usable, reason: r.reason };
}
